import React, { useState, useMemo } from 'react';
import { FloatingPanel } from '../ui/Panel';
import {
  LUMBER_SPECIES,
  JOIST_SIZES,
  LOAD_TYPES,
  IRC_SPAN_TABLE,
  ENGINEERED_LUMBER,
  JOIST_SPACING_OPTIONS,
  GRADE_OPTIONS,
} from '../../constants/structural';

const FloorJoistCalculator = ({ onClose, isMobile = false }) => {
  const [spanFeet, setSpanFeet] = useState(14);
  const [spanInches, setSpanInches] = useState(0);
  const [spacing, setSpacing] = useState(16);
  const [loadType, setLoadType] = useState('living');
  const [species, setSpecies] = useState('spf');
  const [grade, setGrade] = useState('#2');
  const [showEngineered, setShowEngineered] = useState(false);

  const spanTotalInches = spanFeet * 12 + spanInches;

  // Look up span table results
  const results = useMemo(() => {
    const tableLoadKey = loadType === 'barn_loft' || loadType === 'deck' ? 'living' : loadType;
    const speciesTable = IRC_SPAN_TABLE[species];
    if (!speciesTable) return [];

    const gradeTable = speciesTable[grade];
    if (!gradeTable) return [];

    const items = [];
    for (const [sizeKey, sizeData] of Object.entries(JOIST_SIZES)) {
      const sizeTable = gradeTable[sizeKey];
      if (!sizeTable) continue;

      const spacingTable = sizeTable[spacing];
      if (!spacingTable) continue;

      const maxSpan = spacingTable[tableLoadKey] || spacingTable.living || 0;
      const passes = maxSpan >= spanTotalInches;
      const utilization = spanTotalInches / maxSpan;

      items.push({
        key: sizeKey,
        label: sizeData.label,
        maxSpan,
        passes,
        utilization,
        margin: ((maxSpan - spanTotalInches) / spanTotalInches) * 100,
      });
    }

    return items;
  }, [spanFeet, spanInches, spacing, loadType, species, grade]);

  // Find recommended size (smallest that passes with >= 10% margin)
  const recommended = useMemo(() => {
    return results.find(r => r.passes && r.margin >= 10) || results.find(r => r.passes);
  }, [results]);

  // Engineered lumber results
  const engineeredResults = useMemo(() => {
    if (!showEngineered) return [];
    const tableLoadKey = loadType === 'barn_loft' || loadType === 'deck' ? 'living' : loadType;
    const items = [];

    for (const [typeKey, typeData] of Object.entries(ENGINEERED_LUMBER)) {
      for (const [sizeKey, sizeData] of Object.entries(typeData.sizes)) {
        const maxSpan = sizeData.maxSpan[tableLoadKey] || sizeData.maxSpan.living || 0;
        const passes = maxSpan >= spanTotalInches;
        const utilization = spanTotalInches / maxSpan;

        items.push({
          key: `${typeKey}-${sizeKey}`,
          type: typeData.label,
          label: sizeData.label,
          maxSpan,
          passes,
          utilization,
          margin: ((maxSpan - spanTotalInches) / spanTotalInches) * 100,
        });
      }
    }

    return items;
  }, [showEngineered, spanTotalInches, loadType]);

  const formatSpan = (inches) => {
    const ft = Math.floor(inches / 12);
    const inch = inches % 12;
    return inch > 0 ? `${ft}'-${inch}"` : `${ft}'-0"`;
  };

  const getVelocityColor = (utilization) => {
    if (utilization <= 0.7) return '#4caf50'; // green - plenty of margin
    if (utilization <= 0.85) return '#8bc34a'; // light green
    if (utilization <= 0.95) return '#ff9800'; // amber - close to limit
    if (utilization <= 1.0) return '#ff5722'; // orange-red - at limit
    return '#f44336'; // red - exceeds
  };

  const inputStyle = {
    padding: '6px 8px',
    background: '#1a1f24',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#fff',
    fontSize: isMobile ? '12px' : '12px',
    outline: 'none',
    width: '100%',
  };

  const labelStyle = {
    color: '#8090a0',
    fontSize: '10px',
    marginBottom: '3px',
    display: 'block',
  };

  const sectionTitle = {
    color: '#6080a0',
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    paddingBottom: '4px',
    marginBottom: '8px',
    marginTop: '12px',
  };

  return (
    <FloatingPanel
      title="Floor Joist Calculator"
      onClose={onClose}
      x={isMobile ? 10 : 60}
      y={isMobile ? 60 : 100}
      width={320}
      maxHeight={600}
      isMobile={isMobile}
      style={{ borderColor: 'rgba(76,175,80,0.3)' }}
    >
      <div style={{ padding: isMobile ? '8px' : '12px' }}>
        {/* Span Input */}
        <div style={sectionTitle}>Span</div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <span style={labelStyle}>Feet</span>
            <input
              type="number"
              min={1}
              max={40}
              value={spanFeet}
              onChange={(e) => setSpanFeet(Math.max(1, Math.min(40, parseInt(e.target.value) || 0)))}
              style={inputStyle}
            />
          </div>
          <div style={{ flex: 1 }}>
            <span style={labelStyle}>Inches</span>
            <input
              type="number"
              min={0}
              max={11}
              value={spanInches}
              onChange={(e) => setSpanInches(Math.max(0, Math.min(11, parseInt(e.target.value) || 0)))}
              style={inputStyle}
            />
          </div>
        </div>

        {/* Spacing */}
        <div style={{ marginBottom: '8px' }}>
          <span style={labelStyle}>Joist Spacing</span>
          <div style={{ display: 'flex', gap: '4px' }}>
            {JOIST_SPACING_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSpacing(opt.value)}
                style={{
                  flex: 1,
                  padding: '6px',
                  background: spacing === opt.value ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${spacing === opt.value ? 'rgba(76,175,80,0.4)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '4px',
                  color: spacing === opt.value ? '#4caf50' : '#8090a0',
                  fontSize: '11px',
                  fontWeight: spacing === opt.value ? '600' : '400',
                  cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Load Type */}
        <div style={{ marginBottom: '8px' }}>
          <span style={labelStyle}>Load Type</span>
          <select
            value={loadType}
            onChange={(e) => setLoadType(e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {Object.entries(LOAD_TYPES).map(([key, lt]) => (
              <option key={key} value={key}>
                {lt.label} ({lt.live} + {lt.dead} psf)
              </option>
            ))}
          </select>
        </div>

        {/* Species & Grade */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <div style={{ flex: 2 }}>
            <span style={labelStyle}>Species</span>
            <select
              value={species}
              onChange={(e) => setSpecies(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {Object.entries(LUMBER_SPECIES).map(([key, sp]) => (
                <option key={key} value={key}>{sp.label}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <span style={labelStyle}>Grade</span>
            <select
              value={grade}
              onChange={(e) => setGrade(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer' }}
            >
              {GRADE_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>{g.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Engineered Toggle */}
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          color: '#8090a0',
          fontSize: '11px',
          cursor: 'pointer',
          marginBottom: '4px',
        }}>
          <input
            type="checkbox"
            checked={showEngineered}
            onChange={(e) => setShowEngineered(e.target.checked)}
            style={{ accentColor: '#4caf50' }}
          />
          Show engineered lumber options
        </label>

        {/* Results */}
        <div style={sectionTitle}>
          Results — {formatSpan(spanTotalInches)} span @ {spacing}" OC
        </div>

        {results.length === 0 ? (
          <div style={{ color: '#666', fontSize: '11px', padding: '8px 0' }}>
            No span data available for this combination.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {results.map((r) => {
              const isRec = recommended && recommended.key === r.key;
              return (
                <div
                  key={r.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 8px',
                    background: isRec
                      ? 'rgba(76,175,80,0.15)'
                      : r.passes
                        ? 'rgba(255,255,255,0.03)'
                        : 'rgba(244,67,54,0.08)',
                    border: `1px solid ${isRec ? 'rgba(76,175,80,0.4)' : r.passes ? 'rgba(255,255,255,0.06)' : 'rgba(244,67,54,0.2)'}`,
                    borderRadius: '4px',
                  }}
                >
                  {/* Size label */}
                  <span style={{
                    color: r.passes ? '#fff' : '#666',
                    fontWeight: isRec ? '700' : '500',
                    fontSize: '12px',
                    minWidth: '36px',
                  }}>
                    {r.label}
                  </span>

                  {/* Utilization bar */}
                  <div style={{
                    flex: 1,
                    height: '8px',
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(100, r.utilization * 100)}%`,
                      height: '100%',
                      background: getVelocityColor(r.utilization),
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>

                  {/* Max span */}
                  <span style={{
                    color: r.passes ? '#aaa' : '#666',
                    fontSize: '10px',
                    minWidth: '50px',
                    textAlign: 'right',
                  }}>
                    {formatSpan(r.maxSpan)}
                  </span>

                  {/* Pass/Fail */}
                  <span style={{
                    fontSize: '11px',
                    minWidth: '16px',
                    textAlign: 'center',
                  }}>
                    {r.passes ? (isRec ? '★' : '✓') : '✗'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Recommended callout */}
        {recommended && (
          <div style={{
            marginTop: '8px',
            padding: '8px',
            background: 'rgba(76,175,80,0.1)',
            border: '1px solid rgba(76,175,80,0.3)',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#8bc34a',
          }}>
            <strong>Recommended: {recommended.label}</strong>
            <span style={{ color: '#6a8a6a', marginLeft: '8px' }}>
              Max span {formatSpan(recommended.maxSpan)} ({Math.round(recommended.margin)}% margin)
            </span>
          </div>
        )}

        {/* Engineered Results */}
        {showEngineered && engineeredResults.length > 0 && (
          <>
            <div style={sectionTitle}>Engineered Options</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {engineeredResults.map((r) => (
                <div
                  key={r.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 8px',
                    background: r.passes ? 'rgba(255,255,255,0.03)' : 'rgba(244,67,54,0.08)',
                    border: `1px solid ${r.passes ? 'rgba(255,255,255,0.06)' : 'rgba(244,67,54,0.2)'}`,
                    borderRadius: '4px',
                  }}
                >
                  <span style={{
                    color: r.passes ? '#fff' : '#666',
                    fontWeight: '500',
                    fontSize: '11px',
                    minWidth: '100px',
                  }}>
                    {r.label}
                  </span>

                  <div style={{
                    flex: 1,
                    height: '8px',
                    background: 'rgba(255,255,255,0.06)',
                    borderRadius: '4px',
                    overflow: 'hidden',
                  }}>
                    <div style={{
                      width: `${Math.min(100, r.utilization * 100)}%`,
                      height: '100%',
                      background: getVelocityColor(r.utilization),
                      borderRadius: '4px',
                      transition: 'width 0.3s ease',
                    }} />
                  </div>

                  <span style={{
                    color: r.passes ? '#aaa' : '#666',
                    fontSize: '10px',
                    minWidth: '50px',
                    textAlign: 'right',
                  }}>
                    {formatSpan(r.maxSpan)}
                  </span>

                  <span style={{ fontSize: '11px', minWidth: '16px', textAlign: 'center' }}>
                    {r.passes ? '✓' : '✗'}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Load info footer */}
        <div style={{
          marginTop: '12px',
          padding: '6px 8px',
          background: 'rgba(255,255,255,0.03)',
          borderRadius: '4px',
          fontSize: '10px',
          color: '#556',
        }}>
          {LOAD_TYPES[loadType]?.description}
          <br />
          Live: {LOAD_TYPES[loadType]?.live} psf + Dead: {LOAD_TYPES[loadType]?.dead} psf
          <br />
          Deflection limit: L/{LOAD_TYPES[loadType]?.deflectionLimit}
          <br />
          <span style={{ color: '#445', fontStyle: 'italic' }}>
            Based on IRC span tables. Verify with local codes.
          </span>
        </div>
      </div>
    </FloatingPanel>
  );
};

export default FloorJoistCalculator;
